import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Search, Building2, MapPin, Package, Save, Send, ChevronDown, ChevronUp, PenLine, UserX, CheckCircle2, UserPlus, X as XIcon } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { SignaturePadModal } from '../../components/mobile/SignaturePadModal';
import { NumberInput } from '../../components/NumberInput';
import { useConfirm } from '../../context/ConfirmContext';

// Draft key is per-user to prevent cross-account leakage when switching
// accounts on the same browser/device (e.g., kiosk shared device).
// Falls back to '_anon' only if the user context isn't available yet.
const DRAFT_STORAGE_PREFIX = 'mobile_workorder_draft';
const draftKeyFor = (user) => `${DRAFT_STORAGE_PREFIX}_${user?.id || user?.email || user?.username || '_anon'}`;

export const MobileWorkOrderFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user, hasRole } = useAuth();
  const confirm = useConfirm();
  const isNew = !id || id === 'nuovo';
  const canSeeDetails = hasRole(['ADMIN', 'MANAGER']);
  const isShared = user?.is_shared || user?.role === 'SHARED';

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [items, setItems] = useState([]);
  // Selectable real employees (only used by shared kiosk accounts)
  const [selectableEmployees, setSelectableEmployees] = useState([]);
  const [onBehalfOfId, setOnBehalfOfId] = useState('');
  const [onBehalfOfSearch, setOnBehalfOfSearch] = useState('');

  const [workOrderDate, setWorkOrderDate] = useState(new Date().toISOString().split('T')[0]);
  const [workOrderStatus, setWorkOrderStatus] = useState('DRAFT');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedSite, setSelectedSite] = useState(null);
  const [lines, setLines] = useState([]);
  const [notes, setNotes] = useState('');
  const [workOrderId, setWorkOrderId] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showItemPicker, setShowItemPicker] = useState(false);
  const [itemSearch, setItemSearch] = useState('');

  // Signature state
  const [signaturePadOpen, setSignaturePadOpen] = useState(false);
  const [signatureBase64, setSignatureBase64] = useState(null); // string (no data: prefix)
  const [signerName, setSignerName] = useState('');
  const [customerNotPresent, setCustomerNotPresent] = useState(false);
  const [notPresentReason, setNotPresentReason] = useState('');
  const [signedAt, setSignedAt] = useState(null);
  // Recipient contact (for signature attribution and/or email copy)
  const [selectedContactId, setSelectedContactId] = useState('');
  const [sendEmailCopy, setSendEmailCopy] = useState(false);
  const [contactPickerOpen, setContactPickerOpen] = useState(false);
  // Quick "new contact" inline form
  const [newContactOpen, setNewContactOpen] = useState(false);
  const [newContactFirst, setNewContactFirst] = useState('');
  const [newContactLast, setNewContactLast] = useState('');
  const [newContactEmail, setNewContactEmail] = useState('');
  const [creatingContact, setCreatingContact] = useState(false);

  useEffect(() => { loadInitialData(); }, []); // eslint-disable-line

  const loadInitialData = async () => {
    try {
      const requests = [api.getCustomers(), api.getItems(true)];
      if (isShared) requests.push(api.getSelectableEmployees());
      const results = await Promise.all(requests);
      const customersRes = results[0];
      const itemsRes = results[1];
      if (isShared) setSelectableEmployees(results[2].data || []);
      setCustomers(customersRes.data);
      setItems(itemsRes.data);

      if (!isNew) {
        await loadWorkOrder(customersRes.data, itemsRes.data);
      } else {
        // Try restore draft (user-scoped to prevent cross-account leakage)
        try {
          // Defensive cleanup of any draft saved by a previous account on this device
          Object.keys(localStorage).forEach((k) => {
            if (k === 'mobile_workorder_draft') {
              localStorage.removeItem(k); // legacy global key
            } else if (k.startsWith('mobile_workorder_draft_') && k !== draftKeyFor(user)) {
              localStorage.removeItem(k);
            }
          });
          const saved = localStorage.getItem(draftKeyFor(user));
          if (saved) {
            const draft = JSON.parse(saved);
            if (Date.now() - (draft.timestamp || 0) < 24 * 60 * 60 * 1000) {
              if (draft.workOrderDate) setWorkOrderDate(draft.workOrderDate);
              if (draft.notes) setNotes(draft.notes);
              if (draft.selectedCustomer) {
                const c = customersRes.data.find(x => x.id === draft.selectedCustomer.id);
                if (c) {
                  setSelectedCustomer(c);
                  const [sitesRes, contactsRes] = await Promise.all([
                    api.getCustomerSites(c.id),
                    api.getCustomerContacts(c.id).catch(() => ({ data: [] })),
                  ]);
                  setSites(sitesRes.data);
                  setContacts(contactsRes.data || []);
                  if (draft.selectedSite) {
                    const s = sitesRes.data.find(x => x.id === draft.selectedSite.id);
                    if (s) setSelectedSite(s);
                  }
                }
              }
              if (draft.lines?.length > 0) {
                setLines(draft.lines.map(l => ({
                  ...l,
                  item_name: itemsRes.data.find(i => i.id === l.item_id)?.name || l.item_name || 'Articolo'
                })));
              }
              if (draft.step) setStep(draft.step);
            } else {
              localStorage.removeItem(draftKeyFor(user));
            }
          }
        } catch(e) { localStorage.removeItem(draftKeyFor(user)); }
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const loadWorkOrder = async (customersData, itemsData) => {
    try {
      const response = await api.getWorkOrder(id);
      const wo = response.data;
      setWorkOrderId(wo.id);
      setWorkOrderStatus(wo.status || 'DRAFT');
      setNotes(wo.notes || '');
      setWorkOrderDate(wo.work_date || wo.created_at?.split('T')[0] || new Date().toISOString().split('T')[0]);
      if (wo.created_on_behalf_of) setOnBehalfOfId(wo.created_on_behalf_of);

      const customer = customersData.find(c => c.id === wo.customer_id);
      if (customer) setSelectedCustomer(customer);

      // Restore signature state from server
      if (wo.signature_base64) {
        setSignatureBase64(wo.signature_base64);
        setSignerName(wo.signer_name || '');
        setSignedAt(wo.signed_at || null);
        setCustomerNotPresent(false);
      } else if (wo.customer_not_present) {
        setCustomerNotPresent(true);
        setNotPresentReason(wo.not_present_reason || '');
        setSignedAt(wo.signed_at || null);
      }

      if (wo.customer_id) {
        const [sitesRes, contactsRes] = await Promise.all([
          api.getCustomerSites(wo.customer_id),
          api.getCustomerContacts(wo.customer_id).catch(() => ({ data: [] })),
        ]);
        setSites(sitesRes.data);
        setContacts(contactsRes.data || []);
        if (wo.site_id) {
          const site = sitesRes.data.find(s => s.id === wo.site_id);
          if (site) setSelectedSite(site);
        }
      }

      const loadedLines = wo.lines || [];
      setLines(loadedLines.map(line => {
        const item = itemsData.find(i => i.id === line.item_id);
        const isFree = !!line.is_free_article;
        return {
          id: line.id,
          item_id: line.item_id,
          // Free articles keep their own snapshot of name/unit; catalog articles
          // resolve from the items list.
          item_name: isFree ? (line.item_name || 'Articolo libero') : (item?.name || line.item_name || 'Articolo'),
          quantity: line.quantity,
          line_notes: line.line_notes || '',
          unit: isFree ? (line.item_unit || line.unit || '') : (item?.unit || line.item_unit || ''),
          item_unit: line.item_unit || (isFree ? (line.unit || '') : (item?.unit || '')),
          mwst_rate_snapshot: line.mwst_rate_snapshot ?? null,
          is_free_article: isFree,
          assigned_unit_price_chf: line.assigned_unit_price_chf || 0,
          discount_percent: line.discount_percent || 0,
          net_amount: line.net_amount || 0,
        };
      }));

      if (loadedLines.length > 0) setStep(2);
    } catch (error) {
      console.error('Failed to load work order:', error);
      toast.error('Errore nel caricamento bollettino');
      navigate('/m/bollettini');
    }
  };

  // Auto-save draft to localStorage
  useEffect(() => {
    if (isNew && (selectedCustomer || lines.length > 0)) {
      const timeout = setTimeout(() => {
        localStorage.setItem(draftKeyFor(user), JSON.stringify({
          step, workOrderDate, selectedCustomer, selectedSite, lines, notes, timestamp: Date.now()
        }));
      }, 1000);
      return () => clearTimeout(timeout);
    }
  }, [isNew, step, workOrderDate, selectedCustomer, selectedSite, lines, notes]);

  const handleCustomerSelect = async (customer) => {
    if (isShared && !onBehalfOfId) {
      toast.error("Seleziona prima l'operatore");
      return;
    }
    setSelectedCustomer(customer);
    setSelectedSite(null);
    setSelectedContactId('');
    try {
      const [sitesRes, contactsRes] = await Promise.all([
        api.getCustomerSites(customer.id),
        api.getCustomerContacts(customer.id),
      ]);
      setSites(sitesRes.data);
      setContacts(contactsRes.data || []);
    } catch (e) {
      setSites([]);
      setContacts([]);
    }
    setStep(1);
  };

  const handleSiteSelect = (site) => { setSelectedSite(site); setStep(2); };
  const handleSkipSite = () => { setSelectedSite(null); setStep(2); };

  const addLine = (item) => {
    setLines([...lines, {
      id: null,
      item_id: item.id,
      item_name: item.name,
      quantity: 1,
      line_notes: '',
      unit: item.unit || '',
      assigned_unit_price_chf: item.standard_price_chf || 0,
      discount_percent: 0,
    }]);
    setShowItemPicker(false);
    setItemSearch('');
  };

  // Add a free article line. Only ADMIN/MANAGER are allowed to do this
  // (employees/shared kiosks must always pick from the catalog).
  // Unit and IVA are intentionally left empty: the user picks them inline
  // in the article card. Unit is mandatory before save.
  const addFreeLine = (name) => {
    setLines(prev => [...prev, {
      id: null,
      item_id: null,
      item_name: name,
      quantity: 1,
      line_notes: '',
      unit: '',
      item_unit: '',
      mwst_rate_snapshot: null,
      assigned_unit_price_chf: null,
      discount_percent: 0,
      is_free_article: true,
    }]);
    setShowItemPicker(false);
    setItemSearch('');
  };

  const updateLine = (index, field, value) => {
    const newLines = [...lines];
    newLines[index][field] = value;
    setLines(newLines);
  };

  const removeLine = (index) => { setLines(lines.filter((_, i) => i !== index)); };

  // Build the effective contact list shown in the dropdown.
  // Always include a synthetic "customer self" entry when the customer has an email,
  // so even customers with 0 contacts can be used as the reference person.
  const customerSelfContact = (() => {
    if (!selectedCustomer) return null;
    const email = selectedCustomer.contact_email || selectedCustomer.billing_email || '';
    return {
      id: `__customer__${selectedCustomer.id}`,
      first_name: selectedCustomer.name_or_company || '',
      last_name: '',
      email,
      _isCustomerSelf: true,
    };
  })();
  const effectiveContacts = customerSelfContact ? [customerSelfContact, ...contacts] : contacts;

  // Auto-select default contact when entering step 3:
  // 1) site manager (if site has manager_contact_id)
  // 2) first real contact with an email
  // 3) "customer self" virtual entry (name_or_company + contact_email/billing_email)
  useEffect(() => {
    if (step !== 3 || selectedContactId) return;
    if (effectiveContacts.length === 0) return;
    let pick = null;
    if (selectedSite?.manager_contact_id) {
      pick = contacts.find(c => c.id === selectedSite.manager_contact_id);
    }
    if (!pick) pick = contacts.find(c => !!c.email);
    if (!pick) pick = customerSelfContact;
    if (pick) {
      setSelectedContactId(pick.id);
      const fullName = `${pick.first_name || ''} ${pick.last_name || ''}`.trim();
      if (!signatureBase64) setSignerName(fullName);
      if (pick.email) setSendEmailCopy(true);
    }
  }, [step, contacts, selectedSite, selectedContactId, signatureBase64, customerSelfContact, effectiveContacts.length]);

  const handleResetForm = async () => {
    const ok = await confirm({
      title: 'Elimina bollettino',
      description: 'Tutti i dati inseriti per questo bollettino andranno persi. Vuoi davvero procedere?',
      confirmText: 'Elimina',
      destructive: true,
    });
    if (!ok) return;
    // Cancel existing WO if any (only DRAFT / NEEDS_CORRECTION can be cancelled mid-edit)
    if (workOrderId && !isNew) {
      try {
        await api.cancelWorkOrder(workOrderId);
      } catch (e) {
        // If cancel fails (e.g. already submitted), still allow client-side reset
        console.warn('[reset-form] cancelWorkOrder failed (proceeding with local reset):', e);
      }
    }
    // Wipe state
    setSelectedCustomer(null);
    setSelectedSite(null);
    setLines([]);
    setNotes('');
    setWorkOrderDate(new Date().toISOString().split('T')[0]);
    setSignatureBase64(null);
    setSignerName('');
    setSignedAt(null);
    setCustomerNotPresent(false);
    setNotPresentReason('');
    setSelectedContactId('');
    setSendEmailCopy(false);
    setNewContactOpen(false);
    setNewContactFirst(''); setNewContactLast(''); setNewContactEmail('');
    setContacts([]);
    setSites([]);
    setOnBehalfOfId('');
    setOnBehalfOfSearch('');
    // Wipe draft (user-scoped)
    localStorage.removeItem(draftKeyFor(user));
    // Restart from scratch (or back to list when editing existing)
    if (isNew) {
      setStep(1);
    } else {
      navigate('/m/bollettini');
    }
  };

  const handleCreateContact = async () => {
    if (!selectedCustomer) return;
    const fn = newContactFirst.trim();
    const ln = newContactLast.trim();
    const em = newContactEmail.trim();
    if (!fn && !ln) {
      toast.error('Inserisci almeno nome o cognome');
      return;
    }
    setCreatingContact(true);
    try {
      const payload = { first_name: fn, last_name: ln };
      if (em) payload.email = em;
      const res = await api.createCustomerContact(selectedCustomer.id, payload);
      const created = res.data;
      const newList = [...contacts, created];
      setContacts(newList);
      setSelectedContactId(created.id);
      const fullName = `${created.first_name || ''} ${created.last_name || ''}`.trim();
      if (!signatureBase64) setSignerName(fullName);
      // Default email-copy ON when the new contact has an email
      setSendEmailCopy(!!created.email);
      // Reset form
      setNewContactFirst(''); setNewContactLast(''); setNewContactEmail('');
      setNewContactOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nella creazione del contatto');
    } finally {
      setCreatingContact(false);
    }
  };

  const handleBack = () => {
    if (step > 1) { setStep(step - 1); }
    else { navigate('/m/bollettini'); }
  };

  const handleSave = async (andSubmit = false) => {
    if (!selectedCustomer) { toast.error('Seleziona un cliente'); setStep(1); return; }
    if (lines.length === 0) { toast.error('Aggiungi almeno un articolo'); return; }
    if (isShared && !onBehalfOfId) {
      toast.error("Seleziona l'operatore che sta compilando il bollettino");
      return;
    }
    // Block save if any free article still lacks its unit of measure.
    const missingUnit = lines.filter(
      (l) => l.is_free_article && !(l.item_unit || l.unit)
    );
    if (missingUnit.length > 0) {
      const names = missingUnit.map(l => `“${l.item_name || '—'}”`).join(', ');
      toast.error(`Imposta l'unità di misura per: ${names}`);
      return;
    }

    // Signature gate when submitting for review
    if (andSubmit && !signatureBase64 && !customerNotPresent) {
      toast.error("Per inviare in revisione: far firmare il cliente oppure spuntare 'Cliente non presente'.");
      return;
    }

    setSaving(true);
    try {
      let woId = workOrderId;
      // Helper: payload to send for a line. Free articles send item_id=null and
      // include name/unit/MWST/is_free_article so the backend stores them as-is.
      const linePayload = (line) => (line.is_free_article ? {
        item_id: null,
        item_name: line.item_name,
        item_unit: line.item_unit || line.unit,
        mwst_rate_snapshot: line.mwst_rate_snapshot ?? null,
        is_free_article: true,
        quantity: line.quantity,
        date: workOrderDate,
        line_notes: line.line_notes || '',
        discount_percent: line.discount_percent || 0,
      } : {
        item_id: line.item_id,
        quantity: line.quantity,
        date: workOrderDate,
        line_notes: line.line_notes || '',
        discount_percent: line.discount_percent || 0,
        assigned_unit_price_chf: line.assigned_unit_price_chf || 0,
      });
      if (isNew || !woId) {
        const woResponse = await api.createWorkOrder({
          customer_id: selectedCustomer.id,
          site_id: selectedSite?.id || null,
          notes, work_date: workOrderDate,
          ...(isShared ? { created_on_behalf_of: onBehalfOfId } : {}),
        });
        woId = woResponse.data.id;
        setWorkOrderId(woId);
        for (const line of lines) {
          await api.addWorkOrderLine(woId, linePayload(line));
        }
      } else {
        await api.updateWorkOrder(woId, {
          customer_id: selectedCustomer.id,
          site_id: selectedSite?.id || null,
          notes, work_date: workOrderDate
        });
        for (const line of lines) {
          if (line.id) {
            // Use updateWorkOrderLine only when the catalog item_id is present; free
            // articles cannot change their item_id, so we just patch their fields.
            const updPayload = line.is_free_article ? {
              quantity: line.quantity,
              date: workOrderDate,
              line_notes: line.line_notes || '',
              discount_percent: line.discount_percent || 0,
              item_name: line.item_name,
              item_unit: line.item_unit || line.unit,
              mwst_rate_snapshot: line.mwst_rate_snapshot ?? null,
            } : {
              item_id: line.item_id, quantity: line.quantity, date: workOrderDate,
              line_notes: line.line_notes || '', discount_percent: line.discount_percent || 0,
              assigned_unit_price_chf: line.assigned_unit_price_chf || 0,
            };
            await api.updateWorkOrderLine(woId, line.id, updPayload);
          } else {
            await api.addWorkOrderLine(woId, linePayload(line));
          }
        }
      }

      // Persist signature / not-present marker if any was captured locally
      if (signatureBase64) {
        await api.saveWorkOrderSignature(woId, {
          signature_base64: signatureBase64,
          signer_name: signerName || '',
          customer_not_present: false,
          device: 'mobile',
        });
      } else if (customerNotPresent) {
        await api.saveWorkOrderSignature(woId, {
          customer_not_present: true,
          not_present_reason: notPresentReason || '',
          device: 'mobile',
        });
      }

      localStorage.removeItem(draftKeyFor(user));
      if (andSubmit) {
        await api.submitWorkOrder(woId);
        // Optional email copy (works for both real contacts and the synthetic customer entry)
        if (sendEmailCopy && selectedContactId) {
          const c = effectiveContacts.find(x => x.id === selectedContactId);
          if (c?.email) {
            try {
              await api.sendWorkOrderEmail(woId, {
                email: c.email,
                name: `${c.first_name || ''} ${c.last_name || ''}`.trim(),
              });
            } catch (e) {
              // Email is best-effort — failure shouldn't block the submit flow
              console.warn('[submit] sendWorkOrderEmail failed (non-blocking):', e);
            }
          }
        }
        toast.success('Bollettino inviato per revisione!');
      } else {
        toast.success('Bollettino salvato come bozza');
      }
      navigate('/m/bollettini');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-slate-400">Caricamento...</div>;
  }

  const filteredCustomers = customers.filter(c =>
    c.name_or_company.toLowerCase().includes(customerSearch.toLowerCase())
  );

  const filteredItems = items.filter(i =>
    i.name.toLowerCase().includes(itemSearch.toLowerCase())
  );

  const isReadOnly = workOrderStatus !== 'DRAFT' && workOrderStatus !== 'NEEDS_CORRECTION';
  // Once signed (or marked customer-not-present), articles are LOCKED to preserve the signature.
  // To modify them, the signature must be cleared/redone.
  const isLockedBySignature = !isReadOnly && (!!signatureBase64 || customerNotPresent);
  const articlesLocked = isReadOnly || isLockedBySignature;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="mobile-work-order-form">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <button onClick={handleBack} className="p-1 -ml-1" data-testid="back-btn">
            <ArrowLeft className="h-5 w-5 text-slate-600" />
          </button>
          <h1 className="font-bold text-slate-900">{isNew ? 'Nuovo Bollettino' : isReadOnly ? workOrderStatus : 'Modifica Bollettino'}</h1>
          <span className="text-xs text-slate-400">Passo {step}/3</span>
        </div>
        {/* Progress bar */}
        <div className="flex gap-1 mt-2">
          {[1, 2, 3].map(s => (
            <div key={s} className={`h-1 flex-1 rounded-full ${s <= step ? 'bg-blue-600' : 'bg-slate-200'}`} />
          ))}
        </div>
      </div>

      <div className="px-4 py-4">
        {/* Step 1: Operator (shared only) + Date + Customer */}
        {step === 1 && (
          <div className="space-y-4" data-testid="step-1">
            {isShared && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-2" data-testid="onbehalf-block">
                <div className="text-xs font-semibold text-amber-800 uppercase tracking-wide">Operatore</div>
                <label className="block text-sm font-medium text-amber-900">Chi sta compilando? *</label>
                {onBehalfOfId ? (
                  // Selected state: show big chip with the chosen name + change action
                  (() => {
                    const selected = selectableEmployees.find(e => e.id === onBehalfOfId);
                    if (!selected) return null;
                    const initials = `${selected.first_name?.[0] || ''}${selected.last_name?.[0] || ''}`.toUpperCase() || '?';
                    return (
                      <button
                        type="button"
                        onClick={() => setOnBehalfOfId('')}
                        className="w-full flex items-center justify-between gap-3 bg-white border-2 border-amber-400 rounded-xl p-3 active:scale-[0.98] transition-transform"
                        data-testid="onbehalf-selected-chip"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="h-10 w-10 rounded-full bg-amber-100 text-amber-800 font-bold flex items-center justify-center shrink-0">
                            {initials}
                          </div>
                          <div className="text-left min-w-0">
                            <div className="font-semibold text-slate-900 truncate">{selected.first_name} {selected.last_name}</div>
                            <div className="text-xs text-amber-700">Tocca per cambiare operatore</div>
                          </div>
                        </div>
                        <span className="text-amber-600 text-sm shrink-0">Cambia ›</span>
                      </button>
                    );
                  })()
                ) : (
                  // Picker state: grid of buttons (touch-friendly) with search
                  <div className="space-y-2" data-testid="onbehalf-picker">
                    {selectableEmployees.length > 6 && (
                      <input
                        type="search"
                        value={onBehalfOfSearch}
                        onChange={(e) => setOnBehalfOfSearch(e.target.value)}
                        placeholder="Cerca per nome…"
                        className="w-full px-3 py-2.5 bg-white rounded-lg border border-amber-300 text-base focus:outline-none focus:ring-2 focus:ring-amber-500"
                        autoCapitalize="words"
                        autoCorrect="off"
                        data-testid="onbehalf-search-input"
                      />
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto">
                      {selectableEmployees
                        .filter(emp => {
                          if (!onBehalfOfSearch.trim()) return true;
                          const q = onBehalfOfSearch.trim().toLowerCase();
                          const full = `${emp.first_name || ''} ${emp.last_name || ''}`.toLowerCase();
                          return full.includes(q);
                        })
                        .map(emp => {
                          const initials = `${emp.first_name?.[0] || ''}${emp.last_name?.[0] || ''}`.toUpperCase() || '?';
                          return (
                            <button
                              key={emp.id}
                              type="button"
                              onClick={() => { setOnBehalfOfId(emp.id); setOnBehalfOfSearch(''); }}
                              className="flex items-center gap-2.5 bg-white border border-amber-200 hover:bg-amber-100 rounded-lg p-2.5 text-left active:scale-[0.97] transition-transform"
                              data-testid={`onbehalf-option-${emp.id}`}
                            >
                              <div className="h-9 w-9 rounded-full bg-amber-100 text-amber-800 font-bold text-sm flex items-center justify-center shrink-0">
                                {initials}
                              </div>
                              <div className="text-sm font-medium text-slate-900 truncate">
                                {emp.first_name} {emp.last_name}
                              </div>
                            </button>
                          );
                        })}
                      {selectableEmployees.filter(emp => {
                        if (!onBehalfOfSearch.trim()) return true;
                        const q = onBehalfOfSearch.trim().toLowerCase();
                        return `${emp.first_name || ''} ${emp.last_name || ''}`.toLowerCase().includes(q);
                      }).length === 0 && (
                        <p className="text-xs text-amber-700 col-span-full py-3 text-center">Nessun operatore trovato.</p>
                      )}
                    </div>
                  </div>
                )}
                <p className="text-[11px] text-amber-700">
                  Il bollettino verrà tracciato a nome dell&apos;operatore selezionato.
                </p>
              </div>
            )}
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <span>Data:</span>
              <input
                type="date"
                value={workOrderDate}
                onChange={(e) => setWorkOrderDate(e.target.value)}
                className="text-blue-600 font-medium bg-transparent focus:outline-none"
                data-testid="workorder-date"
              />
            </div>
            <h2 className="text-lg font-bold text-slate-900">Seleziona Cliente</h2>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Cerca cliente..."
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                onFocus={() => {
                  // Reopen the full list when the user taps the search box again
                  if (selectedCustomer) setSelectedCustomer(null);
                }}
                className="w-full pl-10 pr-4 py-3 bg-white rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="customer-search"
              />
            </div>
            {selectedCustomer ? (
              <button
                onClick={() => { setSelectedCustomer(null); setSelectedSite(null); setCustomerSearch(''); }}
                className="w-full text-left p-4 rounded-xl border border-blue-500 bg-blue-50 transition-all"
                data-testid="selected-customer-pill"
                title="Tocca per cambiare cliente"
              >
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-blue-600" />
                  <span className="font-medium text-sm">{selectedCustomer.name_or_company}</span>
                  <span className="ml-auto text-[10px] uppercase tracking-wide text-blue-600">Cambia</span>
                </div>
                {(selectedCustomer.billing_address_city || selectedCustomer.address_city) && (
                  <div className="text-xs text-slate-400 mt-1 ml-6">{selectedCustomer.billing_address_city || selectedCustomer.address_city}</div>
                )}
              </button>
            ) : (
              <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                {filteredCustomers.map(customer => (
                  <button
                    key={customer.id}
                    onClick={() => handleCustomerSelect(customer)}
                    className="w-full text-left p-4 rounded-xl border border-slate-200 bg-white hover:border-slate-300 transition-all"
                    data-testid={`customer-${customer.id}`}
                  >
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-slate-400" />
                      <span className="font-medium text-sm">{customer.name_or_company}</span>
                    </div>
                    {(customer.billing_address_city || customer.address_city) && (
                      <div className="text-xs text-slate-400 mt-1 ml-6">{customer.billing_address_city || customer.address_city}</div>
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* Project picker shown inline once a customer is selected */}
            {selectedCustomer && (
              <div className="mt-6 space-y-3" data-testid="inline-site-section">
                <h2 className="text-base font-bold text-slate-900">Progetto</h2>
                <button
                  onClick={handleSkipSite}
                  className={`w-full text-left p-4 rounded-xl border transition-all ${
                    !selectedSite
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-slate-200 border-dashed bg-white hover:border-slate-300'
                  }`}
                  data-testid="skip-site-btn"
                >
                  <span className="text-sm text-slate-600">Senza progetto specifico</span>
                </button>
                {sites.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-2">Nessun progetto per questo cliente</p>
                ) : (
                  <div className="space-y-2">
                    {sites.map(site => (
                      <button
                        key={site.id}
                        onClick={() => handleSiteSelect(site)}
                        className={`w-full text-left p-4 rounded-xl border transition-all ${
                          selectedSite?.id === site.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-slate-200 bg-white hover:border-slate-300'
                        }`}
                        data-testid={`site-${site.id}`}
                      >
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-slate-400" />
                          <span className="font-medium text-sm">{site.site_name}</span>
                        </div>
                        {site.address_city && <div className="text-xs text-slate-400 mt-1 ml-6">{site.address_city}</div>}
                      </button>
                    ))}
                  </div>
                )}
                <button
                  onClick={() => setStep(2)}
                  className="w-full py-3 mt-2 bg-blue-600 text-white rounded-xl font-medium active:scale-[0.98]"
                  data-testid="step1-continue-btn"
                >
                  Continua &rarr;
                </button>
              </div>
            )}
          </div>
        )}

        {/* (Step 2 — site only — removed: merged into Step 1) */}
        {false && step === 999 && (
          <div data-testid="step-2-legacy">
            {/* Legacy site step kept for reference; no longer rendered */}
          </div>
        )}

        {/* Step 2: Notes + Articles */}
        {step === 2 && (
          <div className="space-y-4" data-testid="step-2">
            {/* Summary */}
            <div className="bg-blue-50 rounded-xl p-3 text-sm">
              <div className="font-medium text-blue-800">{selectedCustomer?.name_or_company}</div>
              {selectedSite && <div className="text-blue-600 text-xs flex items-center gap-1"><MapPin className="h-3 w-3" />{selectedSite.site_name}</div>}
              {isShared && onBehalfOfId && (
                <div className="text-amber-700 text-xs mt-0.5" data-testid="onbehalf-summary-step-2-articles">
                  Operatore: <strong>{selectableEmployees.find(e => e.id === onBehalfOfId)?.first_name} {selectableEmployees.find(e => e.id === onBehalfOfId)?.last_name}</strong>
                </div>
              )}
              <div className="text-blue-600 text-xs">{new Date(workOrderDate).toLocaleDateString('it-CH')}</div>
            </div>

            {/* Add Item Button */}
            {!articlesLocked && (
              <button
                onClick={() => setShowItemPicker(!showItemPicker)}
                className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-blue-300 rounded-xl text-blue-600 font-medium hover:bg-blue-50 transition-colors"
                data-testid="add-item-btn"
              >
                {showItemPicker ? <ChevronUp className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                {showItemPicker ? 'Chiudi' : 'Aggiungi Articolo'}
              </button>
            )}

            {isLockedBySignature && (
              <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-800" data-testid="articles-locked-banner">
                <PenLine className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>
                  Articoli bloccati: il bollettino è stato {signatureBase64 ? 'firmato' : 'marcato senza firma cliente'}. Per modificare gli articoli rimuovi la firma e firma di nuovo.
                </span>
              </div>
            )}

            {/* Item Picker */}
            {showItemPicker && (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="item-picker">
                <div className="p-3 border-b border-slate-100">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Cerca esistente o crea nuovo articolo"
                      value={itemSearch}
                      onChange={(e) => setItemSearch(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 bg-slate-50 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      data-testid="item-search"
                    />
                  </div>
                </div>
                <div className="max-h-48 overflow-y-auto divide-y divide-slate-50">
                  {filteredItems.map(item => (
                    <button
                      key={item.id}
                      onClick={() => addLine(item)}
                      className="w-full flex items-center gap-3 p-3 hover:bg-secondary text-left"
                      data-testid={`item-${item.id}`}
                    >
                      <Package className="h-4 w-4 text-slate-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{item.name}</div>
                        <div className="text-xs text-slate-400">{item.unit}</div>
                      </div>
                    </button>
                  ))}
                  {/* Free-article entry: only ADMIN/MANAGER can add, and only when the
                      typed text doesn't match an existing item name (case-insensitive). */}
                  {itemSearch.trim() && !filteredItems.some(it => (it.name || '').toLowerCase() === itemSearch.trim().toLowerCase()) && (
                    <button
                      onClick={() => addFreeLine(itemSearch.trim())}
                      className="w-full flex items-center gap-3 p-3 hover:bg-amber-50 bg-amber-50/30 text-left border-t border-amber-100"
                      data-testid="mobile-create-free-article"
                    >
                      <Plus className="h-4 w-4 text-amber-600 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">
                          Usa «<strong className="text-amber-700">{itemSearch.trim()}</strong>»
                        </div>
                        <div className="text-xs text-amber-700">Imposta unità sulla riga</div>
                      </div>
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Lines */}
            {lines.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-sm font-bold text-slate-700">Articoli selezionati ({lines.length})</h3>
                {lines.map((line, index) => (
                  <div key={line.id || `line-tmp-${index}`} className="bg-white rounded-xl border border-slate-200 p-3" data-testid={`line-${index}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{line.item_name}</span>
                        {line.is_free_article && (
                          <span className="inline-flex items-center text-[10px] uppercase tracking-wide font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded" data-testid={`mobile-free-article-badge-${index}`}>
                            Libero
                          </span>
                        )}
                      </div>
                      {!articlesLocked && (
                        <button onClick={() => removeLine(index)} className="p-1 text-red-400 hover:text-red-600">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                          <label className="text-xs text-slate-400">Qtà:</label>
                          <NumberInput
                            value={line.quantity}
                            onChange={(val) => updateLine(index, 'quantity', val)}
                            className="w-20 px-2 py-1 border border-slate-200 rounded-lg text-sm text-center disabled:bg-slate-50 disabled:text-slate-500"
                            min="0"
                            step="0.5"
                            disabled={articlesLocked}
                            data-testid={`qty-${index}`}
                          />
                          <span className="text-xs text-slate-400">{line.unit}</span>
                        </div>
                      {/* Free article: inline unit dropdown — mandatory, highlighted amber when empty */}
                      {line.is_free_article && !articlesLocked && (
                        <div className="flex items-center gap-2">
                          <label className="text-xs text-slate-400">U.M.:</label>
                          <select
                            value={line.item_unit || line.unit || ''}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (!v) return;
                              updateLine(index, 'item_unit', v);
                              updateLine(index, 'unit', v);
                            }}
                            className={`text-sm border rounded px-2 py-1 ${!(line.item_unit || line.unit) ? 'border-amber-400 bg-amber-50' : 'border-slate-200 bg-white'}`}
                            data-testid={`mobile-free-unit-${index}`}
                          >
                            <option value="" disabled>—</option>
                            {[...new Set([
                              ...items.map(it => it.unit).filter(Boolean),
                              'pz', 'ora', 'kg', 'mq', 'mc', 'km', 'lt', 'forfait',
                            ])].map((opt) => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                    {!articlesLocked && (
                      <input
                        type="text"
                        placeholder="Note riga (opzionale)"
                        value={line.line_notes || ''}
                        onChange={(e) => updateLine(index, 'line_notes', e.target.value)}
                        className="mt-2 w-full px-2 py-1.5 bg-slate-50 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-blue-300"
                        data-testid={`note-${index}`}
                      />
                    )}
                    {line.line_notes && articlesLocked && (
                      <p className="mt-1 text-xs text-slate-400 italic">{line.line_notes}</p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Notes */}
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Note generali {!isReadOnly && '(opzionale)'}</label>
              {isReadOnly ? (
                <p className="text-sm text-slate-600 bg-white p-3 rounded-xl border border-slate-200">{notes || '-'}</p>
              ) : (
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Note per questo bollettino..."
                  rows={3}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  data-testid="notes-input"
                />
              )}
            </div>

            {/* End of step 2 — articles + notes block.
                If user is allowed to edit, provide a "Continua" button to step 3. */}
            {!isReadOnly && (
              <button
                onClick={() => setStep(3)}
                className="w-full py-3 mt-2 bg-blue-600 text-white rounded-xl font-medium active:scale-[0.98] disabled:opacity-50"
                disabled={lines.length === 0}
                data-testid="step2-continue-btn"
              >
                Continua &rarr;
              </button>
            )}
            <button onClick={() => setStep(1)} className="w-full py-2 text-sm text-slate-500" data-testid="step2-back-btn">
              &larr; Cambia cliente / progetto
            </button>
          </div>
        )}

        {/* Step 3: Reference person + Signature + Save */}
        {(step === 3 || isReadOnly) && (
          <div className="space-y-4" data-testid="step-3">
            {/* Summary at top of step 3 */}
            {!isReadOnly && (
              <div className="bg-blue-50 rounded-xl p-3 text-sm">
                <div className="font-medium text-blue-800">{selectedCustomer?.name_or_company}</div>
                {selectedSite && <div className="text-blue-600 text-xs flex items-center gap-1"><MapPin className="h-3 w-3" />{selectedSite.site_name}</div>}
                <div className="text-blue-600 text-xs">{new Date(workOrderDate).toLocaleDateString('it-CH')} · {lines.length} articol{lines.length === 1 ? 'o' : 'i'}</div>
              </div>
            )}

            {/* Persona di riferimento */}
            {!isReadOnly && (
              <div data-testid="reference-person-section">
                <label className="text-sm font-medium text-slate-700 block mb-1">Persona di riferimento</label>
                <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    {(() => {
                      const selected = effectiveContacts.find(x => x.id === selectedContactId);
                      const isManager = selected && selectedSite?.manager_contact_id === selected.id;
                      const isSelf = selected?._isCustomerSelf;
                      const fullName = selected ? `${selected.first_name || ''} ${selected.last_name || ''}`.trim() : '';
                      let suffix = '';
                      if (isManager) suffix = 'Resp. Progetto';
                      else if (isSelf) suffix = 'Cliente';
                      const placeholder = effectiveContacts.length === 0 ? 'Nessun contatto — aggiungine uno →' : 'Seleziona contatto…';
                      return (
                        <button
                          type="button"
                          onClick={() => setContactPickerOpen(true)}
                          className="flex-1 flex items-center justify-between gap-2 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-left active:scale-[0.99]"
                          data-testid="signature-contact-select"
                          data-value={selectedContactId}
                        >
                          {selected ? (
                            <span className="flex-1 min-w-0 truncate">
                              <span className="text-slate-800 font-medium">{fullName}</span>
                              {suffix && <span className="text-blue-600 font-medium"> - {suffix}</span>}
                            </span>
                          ) : (
                            <span className="flex-1 min-w-0 truncate text-slate-400">{placeholder}</span>
                          )}
                          <ChevronDown className="h-4 w-4 text-slate-400 flex-shrink-0" />
                        </button>
                      );
                    })()}
                    <button
                      onClick={() => setNewContactOpen(v => !v)}
                      className="flex-shrink-0 inline-flex items-center justify-center h-9 w-9 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 active:scale-95"
                      title="Aggiungi nuovo contatto"
                      data-testid="new-contact-toggle-btn"
                    >
                      {newContactOpen ? <XIcon className="h-4 w-4" /> : <UserPlus className="h-4 w-4" />}
                    </button>
                  </div>

                  {/* Inline new-contact form */}
                  {newContactOpen && (
                    <div className="space-y-2 p-3 bg-slate-50 rounded-lg" data-testid="new-contact-form">
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="text"
                          value={newContactFirst}
                          onChange={(e) => setNewContactFirst(e.target.value)}
                          placeholder="Nome"
                          className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          data-testid="new-contact-first"
                        />
                        <input
                          type="text"
                          value={newContactLast}
                          onChange={(e) => setNewContactLast(e.target.value)}
                          placeholder="Cognome"
                          className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          data-testid="new-contact-last"
                        />
                      </div>
                      <input
                        type="email"
                        value={newContactEmail}
                        onChange={(e) => setNewContactEmail(e.target.value)}
                        placeholder="Email (opzionale)"
                        className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        data-testid="new-contact-email"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={handleCreateContact}
                          disabled={creatingContact || (!newContactFirst.trim() && !newContactLast.trim())}
                          className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium disabled:opacity-50 active:scale-[0.98]"
                          data-testid="new-contact-save-btn"
                        >
                          {creatingContact ? 'Salvataggio...' : 'Aggiungi e usa'}
                        </button>
                        <button
                          onClick={() => { setNewContactOpen(false); setNewContactFirst(''); setNewContactLast(''); setNewContactEmail(''); }}
                          className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
                          data-testid="new-contact-cancel-btn"
                        >
                          Annulla
                        </button>
                      </div>
                    </div>
                  )}

                  {selectedContactId && (() => {
                    const c = effectiveContacts.find(x => x.id === selectedContactId);
                    if (!c) return null;
                    return (
                      <label className={`flex items-center gap-2 text-xs ${c.email ? 'text-slate-600 cursor-pointer' : 'text-slate-400 cursor-not-allowed'}`}>
                        <input
                          type="checkbox"
                          checked={sendEmailCopy && !!c.email}
                          onChange={(e) => setSendEmailCopy(e.target.checked)}
                          disabled={!c.email}
                          className="h-3.5 w-3.5 accent-blue-600"
                          data-testid="send-email-copy-checkbox"
                        />
                        Invia copia per email a {c.email || 'questo contatto (manca email)'}
                      </label>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* Signature section — minimal, no title */}
            {!isReadOnly && (
              <div data-testid="signature-section" className="space-y-2 pt-2">
                {signatureBase64 ? (
                  <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-2" data-testid="signature-preview-block">
                    <div className="flex items-start gap-3">
                      <div className="border border-slate-200 rounded-lg p-2 bg-slate-50 flex-shrink-0">
                        <img
                          src={`data:image/png;base64,${signatureBase64}`}
                          alt="Firma"
                          className="h-20 w-auto max-w-[180px]"
                          data-testid="signature-preview-img"
                        />
                      </div>
                      <div className="flex-1 min-w-0 text-xs text-slate-500">
                        <div className="flex items-center gap-1 text-green-700 font-medium mb-1">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          Firmato
                        </div>
                        {signerName && <div className="truncate">da <strong className="text-slate-700">{signerName}</strong></div>}
                        {signedAt && <div>{new Date(signedAt).toLocaleString('it-CH')}</div>}
                      </div>
                    </div>
                    <div className="flex gap-2 pt-1">
                      <button
                        onClick={() => { setSignatureBase64(null); setSignerName(''); setSignedAt(null); }}
                        className="flex-1 py-2 text-sm text-red-600 border border-red-200 hover:bg-red-50 rounded-lg active:scale-[0.98]"
                        data-testid="signature-remove-btn"
                      >
                        Rimuovi
                      </button>
                    </div>
                  </div>
                ) : customerNotPresent ? (
                  <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-2" data-testid="not-present-block">
                    <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <UserX className="h-4 w-4 text-amber-700 flex-shrink-0" />
                      <span className="text-sm text-amber-800">Cliente non presente</span>
                    </div>
                    <input
                      type="text"
                      value={notPresentReason}
                      onChange={(e) => setNotPresentReason(e.target.value)}
                      placeholder="Motivo (opzionale, es. progetto chiuso)"
                      className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      data-testid="not-present-reason-input"
                    />
                    <button
                      onClick={() => { setCustomerNotPresent(false); setNotPresentReason(''); }}
                      className="w-full py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg"
                      data-testid="not-present-cancel-btn"
                    >
                      Annulla &mdash; il cliente è presente
                    </button>
                  </div>
                ) : (
                  <>
                    <button
                      onClick={() => setSignaturePadOpen(true)}
                      disabled={!selectedContactId}
                      className="w-full flex items-center justify-center gap-2 py-6 bg-blue-50 text-blue-700 border-2 border-dashed border-blue-300 rounded-xl font-medium active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid="open-signature-btn"
                      title={!selectedContactId ? 'Seleziona prima una persona di riferimento' : undefined}
                    >
                      <PenLine className="h-5 w-5" />
                      Far firmare al cliente
                    </button>
                    {!selectedContactId && (
                      <p className="text-[11px] text-slate-500 text-center">
                        Seleziona prima la persona di riferimento per poter firmare
                      </p>
                    )}
                    <label className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={customerNotPresent}
                        onChange={(e) => {
                          setCustomerNotPresent(e.target.checked);
                          if (e.target.checked) {
                            setSignatureBase64(null);
                            setSignerName('');
                          }
                        }}
                        className="h-4 w-4 accent-blue-600"
                        data-testid="not-present-checkbox"
                      />
                      <UserX className="h-4 w-4 text-slate-400" />
                      Cliente non presente
                    </label>
                  </>
                )}
              </div>
            )}

            {/* Save / Submit Buttons */}
            {!isReadOnly && (
              <div className="space-y-2 pt-2 pb-4">
                {(!signatureBase64 && !customerNotPresent) && (
                  <div className="flex items-start gap-2 px-3 py-2.5 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-xs" data-testid="submit-hint">
                    <PenLine className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>Per <strong>inviare in revisione</strong> serve la firma del cliente <em>oppure</em> spuntare &laquo;Cliente non presente&raquo; qui sopra.</span>
                  </div>
                )}
                <button
                  onClick={() => handleSave(true)}
                  disabled={saving || lines.length === 0}
                  className="w-full flex items-center justify-center gap-2 py-3.5 bg-blue-600 text-white rounded-xl font-medium disabled:opacity-50 active:scale-[0.98] transition-transform"
                  data-testid="save-submit-btn"
                >
                  <Send className="h-4 w-4" />
                  {saving ? 'Salvataggio...' : 'Salva e Invia'}
                </button>
                {!isLockedBySignature && (
                  <button
                    onClick={() => handleSave(false)}
                    disabled={saving || lines.length === 0}
                    className="w-full flex items-center justify-center gap-2 py-3 border border-slate-300 text-slate-700 rounded-xl font-medium disabled:opacity-50"
                    data-testid="save-draft-btn"
                  >
                    <Save className="h-4 w-4" />
                    Salva Bozza
                  </button>
                )}
                <button onClick={() => setStep(2)} className="w-full py-2 text-sm text-slate-500">
                  &larr; Modifica articoli
                </button>
                <button
                  onClick={handleResetForm}
                  className="w-full flex items-center justify-center gap-2 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg active:scale-[0.98]"
                  data-testid="delete-form-btn"
                >
                  <Trash2 className="h-4 w-4" />
                  Elimina bollettino
                </button>
              </div>
            )}

            {isReadOnly && (
              <button
                onClick={() => navigate('/m/bollettini')}
                className="w-full py-3 border border-slate-300 text-slate-700 rounded-xl font-medium mt-4"
                data-testid="back-to-list-btn"
              >
                Torna alla lista
              </button>
            )}
          </div>
        )}
      </div>

      <SignaturePadModal
        open={signaturePadOpen}
        onClose={() => setSignaturePadOpen(false)}
        onConfirm={({ signature_base64, signer_name }) => {
          setSignatureBase64(signature_base64);
          setSignerName(signer_name || '');
          setSignedAt(new Date().toISOString());
          setCustomerNotPresent(false);
          setNotPresentReason('');
          setSignaturePadOpen(false);
        }}
        initialSignerName={signerName || selectedCustomer?.name_or_company || ''}
      />

      {/* Contact picker bottom sheet */}
      {contactPickerOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/40 flex items-end"
          onClick={() => setContactPickerOpen(false)}
          data-testid="contact-picker-overlay"
        >
          <div
            className="w-full bg-white rounded-t-2xl max-h-[70vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid="contact-picker-sheet"
          >
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-semibold text-slate-800">Persona di riferimento</h3>
              <button
                onClick={() => setContactPickerOpen(false)}
                className="p-1 -mr-1 text-slate-500"
                data-testid="contact-picker-close-btn"
              >
                <XIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="overflow-y-auto divide-y divide-slate-100">
              {effectiveContacts.length === 0 ? (
                <p className="p-4 text-sm text-slate-400 text-center">Nessun contatto disponibile</p>
              ) : (
                effectiveContacts.map(c => {
                  const isManager = selectedSite?.manager_contact_id === c.id;
                  const isSelf = c._isCustomerSelf;
                  const fullName = `${c.first_name || ''} ${c.last_name || ''}`.trim();
                  const isSelected = c.id === selectedContactId;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => {
                        setSelectedContactId(c.id);
                        if (!signatureBase64) setSignerName(fullName);
                        setSendEmailCopy(!!c.email);
                        setContactPickerOpen(false);
                      }}
                      className={`w-full text-left px-4 py-3 active:bg-slate-50 ${isSelected ? 'bg-blue-50' : ''}`}
                      data-testid={`contact-picker-item-${c.id}`}
                    >
                      <div className="flex items-center gap-2">
                        {isManager && (
                          <span className="text-[10px] font-bold uppercase tracking-wide bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                            Resp. progetto
                          </span>
                        )}
                        {isSelf && (
                          <span className="text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                            Cliente
                          </span>
                        )}
                        <span className="font-medium text-slate-800">{fullName}</span>
                        {isSelected && <CheckCircle2 className="h-4 w-4 text-blue-600 ml-auto flex-shrink-0" />}
                      </div>
                      {c.email && <div className="text-xs text-slate-500 mt-0.5 truncate">{c.email}</div>}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default MobileWorkOrderFormPage;
